import { ShippingMethod } from '@monofront/types';
import invariant from 'tiny-invariant';
import { and, assign, fromPromise, raise, sendTo, setup } from 'xstate';
import { ACTIONS } from './actions';
import { EVENTS } from './events';
import { GUARDS, GUARDS_FUNCTIONS } from './guards';
import { selectorMachine } from './selectorMachine';
import { DELIVERY_BAR_EXTERNAL_SERVICES } from './services';
import { shippingMethodSelectorMachine } from './shippingMethodSelectorMachine';
import { STATES } from './states';
import type {
  DeliveryBarMachineAddress,
  DeliveryBarMachineContext,
  DeliveryBarMachineContextShippingMethod,
  DeliveryBarMachineInput,
  MachineEvents,
} from './types';

const initialState = {
  initial: STATES.TRANSITION_INITIAL_STATE_RESOLVER,
  context: {
    address: null,
    shippingMethod: null,
    eligibleShippingMethods: [],
  },
};

const validateValueBeforeUpdate = <T>(value: T | null, actionName: string) => {
  invariant(value, `value must exist when calling ${actionName}`);
};

const machine = setup({
  types: {
    context: {} as DeliveryBarMachineContext,
    input: {} as DeliveryBarMachineInput,
    events: {} as MachineEvents,
  },

  guards: GUARDS_FUNCTIONS,

  actions: {
    // ADDRESS
    [ACTIONS.CONTEXT_ADDRESS_RESET]: assign({
      address: () => initialState.context.address,
    }),
    [ACTIONS.CONTEXT_ADDRESS_UPDATE]: assign({
      address: ({ event }) => {
        invariant(
          event.type === EVENTS.ADDRESS_SELECTION_FLOW_COMPLETE,
          `Expected ${EVENTS.ADDRESS_SELECTION_FLOW_COMPLETE} event, got ${event.type}`,
        );

        const { value: address } = event.output;

        validateValueBeforeUpdate(address, ACTIONS.CONTEXT_ADDRESS_UPDATE);

        return address;
      },
    }),
    [ACTIONS.CONTEXT_ADDRESS_OVERWRITE]: assign({
      address: ({ event }) => {
        invariant(
          event.type === EVENTS.OVERWRITE_ADDRESS,
          `Expected ${EVENTS.OVERWRITE_ADDRESS} event, got ${event.type}`,
        );

        const { address } = event;

        validateValueBeforeUpdate(address, ACTIONS.CONTEXT_ADDRESS_OVERWRITE);

        return address;
      },
    }),

    // SHIPPING METHOD
    [ACTIONS.CONTEXT_SHIPPING_METHOD_RESET]: assign({
      shippingMethod: initialState.context.shippingMethod,
    }),
    [ACTIONS.CONTEXT_SHIPPING_METHOD_UPDATE]: assign({
      shippingMethod: ({ event }) => {
        invariant(
          event.type === EVENTS.SHIPPING_METHOD_SELECTION_FLOW_COMPLETE,
          `Expected ${EVENTS.SHIPPING_METHOD_SELECTION_FLOW_COMPLETE} event, got ${event.type}`,
        );

        const { shippingMethod } = event.output;

        validateValueBeforeUpdate(
          shippingMethod,
          ACTIONS.CONTEXT_SHIPPING_METHOD_UPDATE,
        );

        return shippingMethod;
      },
    }),
    [ACTIONS.CONTEXT_SHIPPING_METHOD_OVERWRITE]: assign({
      shippingMethod: ({ event }) => {
        invariant(
          event.type === EVENTS.OVERWRITE_SHIPPING_METHOD,
          `Expected ${EVENTS.OVERWRITE_SHIPPING_METHOD} event, got ${event.type}`,
        );

        const { shippingMethod } = event;

        validateValueBeforeUpdate(
          shippingMethod,
          ACTIONS.CONTEXT_SHIPPING_METHOD_OVERWRITE,
        );

        return shippingMethod;
      },
    }),

    // CONTEXT REHYDRATE
    [ACTIONS.CONTEXT_REHYDRATE]: assign({
      address: ({ event }) => {
        invariant(
          event.type === EVENTS.GO_TO_STATE_INITIAL_STATE_RESOLVER,
          `Expected ${EVENTS.GO_TO_STATE_INITIAL_STATE_RESOLVER} event, got ${event.type}`,
        );

        return event.input?.address ?? initialState.context.address;
      },
      shippingMethod: ({ event }) => {
        invariant(
          event.type === EVENTS.GO_TO_STATE_INITIAL_STATE_RESOLVER,
          `Expected ${EVENTS.GO_TO_STATE_INITIAL_STATE_RESOLVER} event, got ${event.type}`,
        );

        return (
          event.input?.shippingMethod ?? initialState.context.shippingMethod
        );
      },
      eligibleShippingMethods: ({ event }) => {
        invariant(
          event.type === EVENTS.GO_TO_STATE_INITIAL_STATE_RESOLVER,
          `Expected ${EVENTS.GO_TO_STATE_INITIAL_STATE_RESOLVER} event, got ${event.type}`,
        );

        return (
          event.input?.eligibleShippingMethods ??
          initialState.context.eligibleShippingMethods
        );
      },
    }),

    // ELIGIBLE SHIPPING METHODS
    [ACTIONS.CONTEXT_ELIGIBLE_SHIPPING_METHOD_RESET]: assign({
      eligibleShippingMethods: initialState.context.eligibleShippingMethods,
    }),
    [ACTIONS.CONTEXT_ELIGIBLE_SHIPPING_METHOD_UPDATE]: assign({
      eligibleShippingMethods: ({ event }) => {
        invariant(
          event.type === EVENTS.FETCH_ELIGIBLE_SHIPPING_METHODS_COMPLETE,
          `Expected ${EVENTS.FETCH_ELIGIBLE_SHIPPING_METHODS_COMPLETE} event, got ${event.type}`,
        );

        if (!Array.isArray(event.output) || !event.output.length) {
          return [];
        }

        // create a serializer for shipping method
        return (event.output as ShippingMethod[]).reduce<
          DeliveryBarMachineContextShippingMethod[]
        >((acc, shippingMethod) => {
          const sameType = acc.find(
            (method) => method.type === shippingMethod.type,
          );

          const config = {
            category: shippingMethod.category,
            aboveAllThresholdsAmount:
              shippingMethod.configuration.aboveAllThresholdsAmount,
            underThresholdAmount:
              shippingMethod.configuration.underThresholdAmount,
            cartTotalThreshold: shippingMethod.configuration.cartTotalThreshold,
            thresholds: shippingMethod.configuration.thresholds,
          };

          if (sameType && sameType.configurations) {
            sameType.configurations.push(config);

            return acc;
          }

          const newConfigurations =
            shippingMethod?.category || shippingMethod?.configuration
              ? [config]
              : [];

          acc.push({
            type: shippingMethod.type,
            carrierId: shippingMethod.tmsCode ?? '',
            configurations: newConfigurations,
          } as DeliveryBarMachineContextShippingMethod);

          return acc;
        }, []);
      },
    }),
  },

  actors: {
    addressSelectorMachine: selectorMachine,
    shippingMethodSelectorMachine: shippingMethodSelectorMachine,

    [DELIVERY_BAR_EXTERNAL_SERVICES.fetchShippingMethods]: fromPromise(
      async (_: { input: { address: DeliveryBarMachineAddress } }) =>
        [] as ShippingMethod[],
    ),
    [DELIVERY_BAR_EXTERNAL_SERVICES.updateCustomerData]: fromPromise(
      async (_: {
        input: {
          address: DeliveryBarMachineAddress;
          shippingMethod: DeliveryBarMachineContextShippingMethod;
        };
        // eslint-disable-next-line @typescript-eslint/no-empty-function
      }) => {},
    ),
    [DELIVERY_BAR_EXTERNAL_SERVICES.validateFormData]: fromPromise(
      async (_: {
        input: {
          address: DeliveryBarMachineAddress;
          shippingMethod: DeliveryBarMachineContextShippingMethod;
        };
        // eslint-disable-next-line @typescript-eslint/no-empty-function
      }) => {},
    ),
    [DELIVERY_BAR_EXTERNAL_SERVICES.checkAddressEligibility]: fromPromise(
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      async (_: { input: { address: DeliveryBarMachineAddress } }) => {},
    ),
    [DELIVERY_BAR_EXTERNAL_SERVICES.rehydrateContext]: fromPromise(
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      async (_: { input: DeliveryBarMachineInput }) => {},
    ),
  },
}).createMachine({
  id: 'delivery-bar',
  initial: initialState.initial,
  actions: {},
  on: {
    [EVENTS.GO_TO_STATE_INITIAL_STATE_RESOLVER]: {
      target: `.${STATES.TRANSITION_INITIAL_STATE_RESOLVER}`,
      actions: ACTIONS.CONTEXT_REHYDRATE,
    },
    [EVENTS.GO_TO_STATE_ADDRESS_SELECTOR]: {
      target: `.${STATES.ADDRESS_SELECTOR}`,
      actions: [
        ACTIONS.CONTEXT_ADDRESS_RESET,
        ACTIONS.CONTEXT_SHIPPING_METHOD_RESET,
      ],
    },
    [EVENTS.GO_TO_STATE_SHIPPING_METHOD_SELECTOR]: {
      target: `.${STATES.SHIPPING_METHOD_SELECTOR}`,
      actions: ACTIONS.CONTEXT_SHIPPING_METHOD_RESET,
    },
    [EVENTS.GO_TO_UPDATE_CUSTOMER_DATA]: {
      target: `.${STATES.TRANSITION_ASYNC_SAVE_CUSTOMER_DATA}`,
    },
    [EVENTS.OVERWRITE_ADDRESS]: {
      actions: [
        ACTIONS.CONTEXT_ADDRESS_OVERWRITE,
        raise({ type: EVENTS.GO_TO_UPDATE_CUSTOMER_DATA }),
      ],
    },
    [EVENTS.OVERWRITE_SHIPPING_METHOD]: {
      actions: [
        ACTIONS.CONTEXT_SHIPPING_METHOD_OVERWRITE,
        raise({ type: EVENTS.GO_TO_UPDATE_CUSTOMER_DATA }),
      ],
    },
  },
  context: ({ input }) => {
    if (!input) {
      return initialState.context;
    }

    const { callbacks, ...contextData } = input;

    return {
      ...initialState.context,
      ...contextData,
      _callbacks: callbacks,
    };
  },
  states: {
    [STATES.TRANSITION_INITIAL_STATE_RESOLVER]: {
      always: [
        {
          guard: and([
            GUARDS.HAS_SELECTED_ADDRESS,
            GUARDS.HAS_SELECTED_SHIPPING_METHOD,
          ]),
          target: STATES.TRANSITION_ASYNC_FETCH_ELIGIBLE_SHIPPING_METHODS,
        },
        {
          target: STATES.ADDRESS_SELECTOR,
        },
      ],
    },

    [STATES.ADDRESS_SELECTOR]: {
      entry: ACTIONS.CONTEXT_ADDRESS_RESET,
      invoke: {
        src: 'addressSelectorMachine',
        id: 'addressSelectorMachine',
        input: null,
        onDone: {
          target: STATES.TRANSITION_ASYNC_FETCH_ELIGIBLE_SHIPPING_METHODS,
          actions: ACTIONS.CONTEXT_ADDRESS_UPDATE,
        },
      },
      on: {
        [EVENTS.ADDRESS_SELECT]: {
          actions: sendTo('addressSelectorMachine', ({ event }) => ({
            type: 'SELECT',
            value: event.address,
          })),
        },
      },
    },

    [STATES.TRANSITION_ASYNC_FETCH_ELIGIBLE_SHIPPING_METHODS]: {
      invoke: {
        src: DELIVERY_BAR_EXTERNAL_SERVICES.fetchShippingMethods,
        id: 'fetchShippingMethodsService',
        input: ({ context: { address } }) => ({
          address: address as DeliveryBarMachineAddress,
        }),
        onDone: [
          {
            guard: and([
              GUARDS.HAS_SELECTED_ADDRESS,
              GUARDS.HAS_SELECTED_SHIPPING_METHOD,
            ]),
            target: STATES.SUMMARY,
            actions: [ACTIONS.CONTEXT_ELIGIBLE_SHIPPING_METHOD_UPDATE],
          },
          {
            target: STATES.SHIPPING_METHOD_SELECTOR,
            actions: ACTIONS.CONTEXT_ELIGIBLE_SHIPPING_METHOD_UPDATE,
          },
        ],
      },
    },

    [STATES.SHIPPING_METHOD_SELECTOR]: {
      entry: ACTIONS.CONTEXT_SHIPPING_METHOD_RESET,
      invoke: {
        src: 'shippingMethodSelectorMachine',
        id: 'shippingMethodSelectorMachine',
        input: null,
        onDone: {
          target: STATES.TRANSITION_ASYNC_SAVE_CUSTOMER_DATA,
          actions: ACTIONS.CONTEXT_SHIPPING_METHOD_UPDATE,
        },
      },
      on: {
        [EVENTS.SHIPPING_METHOD_SELECT]: {
          actions: sendTo('shippingMethodSelectorMachine', ({ event }) => ({
            type: 'SELECT',
            value: event.shippingMethod,
          })),
        },
        [EVENTS.PICKUP_POINT_SELECT]: {
          actions: sendTo('shippingMethodSelectorMachine', ({ event }) => ({
            type: 'SELECT',
            value: event.pickupPoint,
          })),
        },
      },
    },
    [STATES.TRANSITION_ASYNC_SAVE_CUSTOMER_DATA]: {
      invoke: [
        {
          src: DELIVERY_BAR_EXTERNAL_SERVICES.checkAddressEligibility,
          input: ({ context: { address } }) => {
            invariant(
              address != null,
              'Address must not be null when checking address eligibility',
            );

            return {
              address: address as DeliveryBarMachineAddress,
            };
          },
        },
        {
          src: DELIVERY_BAR_EXTERNAL_SERVICES.validateFormData,
          input: ({
            context: { address, shippingMethod, eligibleShippingMethods },
          }) => {
            invariant(
              address != null,
              'Address must not be null when updating customer data',
            );
            invariant(
              shippingMethod != null,
              'Shipping method must not be null when updating customer data',
            );

            const resolvedShippingMethod = eligibleShippingMethods.find(
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore cl-backoffice has an error with method being undefined
              (method) => method.type === shippingMethod.type,
            );

            return {
              address: address as DeliveryBarMachineAddress,
              shippingMethod:
                resolvedShippingMethod as DeliveryBarMachineContextShippingMethod,
            };
          },
          onDone: {
            actions: [
              ({ context }) => {
                const serviceName =
                  DELIVERY_BAR_EXTERNAL_SERVICES.validateFormData;

                context._callbacks?.[serviceName]?.success?.();
              },
            ],
          },
          onError: {
            actions: [
              ({ context }) => {
                const serviceName =
                  DELIVERY_BAR_EXTERNAL_SERVICES.validateFormData;

                context._callbacks?.[serviceName]?.error?.();
              },
            ],
          },
        },
        {
          src: DELIVERY_BAR_EXTERNAL_SERVICES.updateCustomerData,
          input: ({ context: { address, shippingMethod } }) => {
            invariant(
              address != null,
              'Address must not be null when updating customer data',
            );
            invariant(
              shippingMethod != null,
              'Shipping method must not be null when updating customer data',
            );

            return {
              address: address as DeliveryBarMachineAddress,
              shippingMethod:
                shippingMethod as DeliveryBarMachineContextShippingMethod,
            };
          },
          onDone: {
            target: STATES.TRANSITION_INITIAL_STATE_RESOLVER,
          },
        },
      ],
    },
    [STATES.FETCH_SHIPPING_METHODS_ERROR]: {},
    [STATES.SUMMARY]: {},
  },
});

export { initialState, machine };
